<script setup lang="ts">
import {computed, onBeforeMount, onBeforeUnmount} from "vue";
import { useStore } from "vuex";
import {addSizeToDataLayer} from "@/functions/analytics";
import {stripNonNumeric, endsWithSeparator, limitDecimals, toNumber} from "@/functions/numbers";
import DimensionInput from "@/components/UI/DimensionInput.vue";
import DrillHoleConfigurationFactory from "@/Models/DrillHoleConfigurations/DrillHoleConfigurationFactory";
import Length from "../../../source/js/shared/models/Geometry/Length";
import ValidationMessage from "./UI/ValidationMessage.vue";
import MessageContainer from "@/components/UI/MessageContainer.vue";

const store = useStore();

const enclosingRectangle = computed(() => store.getters.enclosingRectangle );
const isDrillHoleValid = computed(() => store.getters.isDrillHoleValid );
const getDrillHoleValidationMessage = computed(() => store.getters.getDrillHoleValidationMessage );
const minimumDrillHoleDiameter = computed(() => store.getters.minimumDrillHoleDiameter );
const lastGeneratedConfiguration = computed(() => store.getters.lastGeneratedConfiguration );

const drillOption = computed(() => store.getters.drillOption);
const drillHoleRadialAmount = computed({
	get: () => store.getters.drillHoleRadialAmount,
	set: (value: string) => {
		if ( ! endsWithSeparator( value ) && value !== '' ) {
			store.commit('updateDrillHoleRadialAmount', toNumber(stripNonNumeric(value)));
			store.commit('updateUsingDefaultDrillHoleDimensions', false);
		}
	}
});

const drillHoleDiameter = computed({
	get: () => store.getters.drillHoleDiameter.in_cm(),
	set: (value: string) => {
		if ( ! endsWithSeparator( value ) && value !== '' ) {
			store.commit('updateDrillHoleDiameter', Length.from_cm( limitDecimals( stripNonNumeric(value), 1 ) ).in_mm() );
			store.commit('updateUsingDefaultDrillHoleDimensions', false);
		}
	}
});

const drillHoleRadialDiameter = computed({
	get: () => store.getters.drillHoleRadialDiameter.round( 0 ).in_cm(),
	set: (value: string) => {
		if ( ! endsWithSeparator( value ) && value !== '' ) {
			store.commit('updateDrillHoleRadialDiameter', Length.from_cm( limitDecimals( stripNonNumeric(value), 1 ) ).in_mm() );
			store.commit('updateUsingDefaultDrillHoleDimensions', false);
		}
	}
});

const drillHoleRadialOffsetX = computed({
	get: () => store.getters.drillHoleRadialOffsetX.round( 0 ).in_cm(),
	set: (value: string) => {
		if ( ! endsWithSeparator( value ) && value !== '' ) {
			store.commit('updateDrillHoleRadialOffsetX', Length.from_cm( limitDecimals( stripNonNumeric(value), 1 ) ).in_mm() );
			store.commit('updateUsingDefaultDrillHoleDimensions', false);
		}
	}
});

const drillHoleRadialOffsetY = computed({
	get: () => store.getters.drillHoleRadialOffsetY.round( 0 ).in_cm(),
	set: (value: string) => {
		if ( ! endsWithSeparator( value ) && value !== '' ) {
			store.commit('updateDrillHoleRadialOffsetY', Length.from_cm( limitDecimals( stripNonNumeric(value), 1 ) ).in_mm() );
			store.commit('updateUsingDefaultDrillHoleDimensions', false);
		}
	}
});
const getCenter = computed(() => store.getters.getCenter).value;
document.addEventListener("focusin", focusChanged);

function focusChanged(event): void {
	store.commit("updateFocusedElement", event.target);
}

function drillHoleFieldsWithMessage(): {key: string, message: string}[] {
	const configuration = new DrillHoleConfigurationFactory().get_configuration( 'radial' );

	// filter out keys with valid dimensions
	const invalidKeys = configuration.get_input_keys().filter((field) => !isDrillHoleValid.value(field));

	// get the data we need to render a notice
	return invalidKeys.map((field) => {
		return {
			key: field,
			message: getDrillHoleValidationMessage.value(field),
		};
	});
}

onBeforeMount(async () => {

	// Don't set defaults if we have an old configuration available for radial drill holes.
	if ( lastGeneratedConfiguration.value.drillholes?.type === 'radial' ) {
		return;
	}

	const radialDiameter = Length.from_cm( enclosingRectangle.value.width ).min( Length.from_cm( enclosingRectangle.value.height ) ).subtract( new Length( 30 ) );
	const center = getCenter(true, 'mm');

	await store.dispatch( 'updateDrillHolesFromConfig', {
		type: 'radial',
		diameter: Length.from_cm( drillHoleDiameter.value ).round( 1 ).in_mm(),
		amount: drillHoleRadialAmount.value,
		position: {
			left: center.x,
			bottom: center.y,
		},
		radialDiameter: radialDiameter.divide( 2 ).round( 0 ).in_mm()
	} );
});

onBeforeUnmount(() => {
	document.removeEventListener("focusin", focusChanged);
});

</script>

<template>
	<div class="input-container">
		<div class="drill-hole-inputs">
			<dimension-input
				inputRef="drillHoleRadialAmount"
				id="drillHoleRadialAmount"
				name="drillHoleRadialAmount"
				inputmode="numeric"
				:min="1"
				:label="$i18n.drillHoleConfiguration.radial.amount"
				:invalid="!isDrillHoleValid('drillHoleRadialAmount')"
				:onBlur="() => addSizeToDataLayer($i18n.drillHoleConfiguration.radial.amount, drillHoleRadialAmount, 'radial-drill-hole')"
				v-model="drillHoleRadialAmount"
				:step="1"
			/>

			<dimension-input
				inputRef="drillHoleDiameter"
				id="drillHoleDiameter"
				name="drillHoleDiameter"
				unit="cm"
				:min="minimumDrillHoleDiameter"
				:label="`${$i18n.drillHoleConfiguration.diameter_drill_hole}`"
				:invalid="!isDrillHoleValid('drillHoleDiameter')"
				:onBlur="() => addSizeToDataLayer($i18n.drillHoleConfiguration.diameter_drill_hole, drillHoleDiameter, 'radial-drill-hole')"
				v-model="drillHoleDiameter"
			/>

			<dimension-input
				inputRef="drillHoleRadialDiameter"
				id="drillHoleRadialDiameter"
				name="drillHoleRadialDiameter"
				unit="cm"
				:label="$i18n.drillHoleConfiguration.radial.diameter_from_center"
				:invalid="!isDrillHoleValid('drillHoleRadialDiameter')"
				:onBlur="() => addSizeToDataLayer($i18n.drillHoleConfiguration.radial.diameter_from_center, drillHoleRadialDiameter, 'radial-drill-hole')"
				v-model="drillHoleRadialDiameter"
			/>

			<dimension-input
				inputRef="drillHoleRadialOffsetX"
				id="drillHoleRadialOffsetX"
				name="drillHoleRadialOffsetX"
				unit="cm"
				:label="$i18n.drillHoleConfiguration.radial.offset_x"
				:invalid="!isDrillHoleValid('drillHoleRadialOffsetX')"
				:onBlur="() => addSizeToDataLayer($i18n.drillHoleConfiguration.radial.offset_x, drillHoleRadialOffsetX, 'radial-drill-hole')"
				v-model="drillHoleRadialOffsetX"
				class="half-width"
			/>

			<dimension-input
				inputRef="drillHoleRadialOffsetY"
				id="drillHoleRadialOffsetY"
				name="drillHoleRadialOffsetY"
				unit="cm"
				:label="$i18n.drillHoleConfiguration.radial.offset_y"
				:invalid="!isDrillHoleValid('drillHoleRadialOffsetY')"
				:onBlur="() => addSizeToDataLayer($i18n.drillHoleConfiguration.radial.offset_y, drillHoleRadialOffsetY, 'radial-drill-hole')"
				v-model="drillHoleRadialOffsetY"
				class="half-width"
			/>
		</div>

		<message-container>
			<validation-message
				v-if="drillOption !== 'none'"
				type="notice"
			>
				{{ $i18n.drillHolesNotice }}
			</validation-message>

			<validation-message
				v-for="field in drillHoleFieldsWithMessage()"
				:key="field.key + '-validation-message'"
				:input-id="field.key"
			>
				{{ field.message }}
			</validation-message>

			<validation-message
				v-if="! isDrillHoleValid('drillHolesMissing')"
			>
				{{ getDrillHoleValidationMessage('drillHolesMissing') }}
			</validation-message>
		</message-container>
	</div>
</template>

<style scoped lang="scss">
.input-container {
	background-color: $brand-blue-background;
	border-radius: 8px;
	padding: 20px;
}

.drill-hole-inputs {
	display: grid;
	grid-template-columns: repeat(6, 1fr);
	gap: 12px;
}

.dimension-input {
	grid-column: span 2;
}

.dimension-input.half-width {
	grid-column: span 3;
}
</style>
